import { PageProps } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'

import type { BlogNodeType, HeroProfileComponentType, Image as ImageType } from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { isGif, isSvg } from '@src/Application/Interfaces/Atoms/Image/Image'
import { Nullable } from '@src/Application/Types/Opt'
import { getCompletePath } from '@src/Application/Utils/StringUtility'
import { getOrganizationSettings } from '@src/Infrastructure/Context/OrganizationContext'

export const getFeaturedImagePath = ({
    image,
    defaultImagePath,
}: {
    image?: Nullable<ImageType>
    defaultImagePath: string
}): string => {
    if (!image?.localFile) return getCompletePath(defaultImagePath)

    if (isSvg(image) || isGif(image)) {
        return image.localFile.publicURL
    }

    return getSrc(image.localFile) ?? getCompletePath(defaultImagePath)
}

export const getBlogPostingSchemaMarkup = (
    props: PageProps<{
        strapiPage: BlogNodeType
        site: { siteMetadata: { siteUrl: string } }
    }>
): string => {
    const { siteUrl } = props.data.site.siteMetadata
    const organization = getOrganizationSettings()

    const heroImage = (props.data.strapiPage.pageComponents as HeroProfileComponentType[]).find(
        (item) => item.backgroundImage
    )?.backgroundImage

    const ogImageUrlPath = organization.siteMetadata[props.data.strapiPage.language as Locale]?.ogImageUrlPath ?? ''
    const featuredImagePath = getFeaturedImagePath({ image: heroImage, defaultImagePath: ogImageUrlPath })
    const featuredImageUrl = `${siteUrl}${featuredImagePath}`

    return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: props.data.strapiPage.metaTitle,
        description: props.data.strapiPage.metaDescription,
        datePublished: props.data.strapiPage.createdAt,
        dateModified: props.data.strapiPage.updatedAt,
        author: {
            '@type': 'Person',
            name: organization.name,
        },
        publisher: {
            '@type': 'Organization',
            name: organization.name,
            logo: {
                '@type': 'ImageObject',
                url: `${siteUrl}${getCompletePath(organization.logoPath)}`,
            },
        },
        image: featuredImageUrl,
    })
}
